/**
 * List of all supported locales
 *
 * The order is important, as it will be used to determine the best match for a given locale.
 */
export const SUPPORTED_LOCALES = [
	'en-US',
	'en-GB',
	'en-IE',
	'fr-FR',
	'fr-BE',
	'fr-CH',
	'de-DE',
	'de-AT',
	'de-CH',
	'nl-NL',
	'nl-BE',
	'it-IT',
	'it-CH',
	'es-ES',
	'pt-PT',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Split each locale into [language, country]
type SplitLocale<L extends string> =
  L extends `${infer Lang}-${infer Country}` ? [Lang, Country] : never;

type AllSplits = SplitLocale<SupportedLocale>;

// Extract languages
export type SupportedLanguage = AllSplits extends [infer Lang, any]
	? Lang & string
	: never;

// Extract countries
export type SupportedCountry = AllSplits extends [any, infer Country]
	? Country & string
	: never;

// Turn them into arrays (deduplicated at runtime)
export const SUPPORTED_LANGUAGES = [
	...new Set(SUPPORTED_LOCALES.map(l => l.split('-')[0])),
] as SupportedLanguage[];

export const SUPPORTED_COUNTRIES = [
	...new Set(SUPPORTED_LOCALES.map(l => l.split('-')[1])),
] as SupportedCountry[];

export const localeName = {
	enUS: 'en-US',
	enGB: 'en-GB',
	enIE: 'en-IE',
	frFR: 'fr-FR',
	frBE: 'fr-BE',
	frCH: 'fr-CH',
	deDE: 'de-DE',
	deAT: 'de-AT',
	deCH: 'de-CH',
	nlNL: 'nl-NL',
	nlBE: 'nl-BE',
	itIT: 'it-IT',
	itCH: 'it-CH',
	esES: 'es-ES',
	ptPT: 'pt-PT',
} as const;

export const SUPPORTED_LOCALE_DEFAULT = 'en-US' as const;

export function getCountryFromLocale(locale: SupportedLocale): SupportedCountry {
	const splitLanguage = locale.split('-');
	return splitLanguage.at(-1) as SupportedCountry;
}

export function getLanguageFromLocale(locale: SupportedLocale): SupportedLanguage {
	const splitLanguage = locale.split('-');
	return splitLanguage[0].toLowerCase() as SupportedLanguage;
}

export const HOSTNAME_LOCALE_REVIEW_PREFIX: Record<string, SupportedLocale> = {
	at: 'de-AT',
	com: 'en-US',
	couk: 'en-GB',
	de: 'de-DE',
	dech: 'de-CH',
	es: 'es-ES',
	fr: 'fr-FR',
	frbe: 'fr-BE',
	frch: 'fr-CH',
	ie: 'en-IE',
	it: 'it-IT',
	itch: 'it-CH',
	nl: 'nl-NL',
	nlbe: 'nl-BE',
	pt: 'pt-PT',
};
